// 소셜 로그인 버튼 3사 묶음.
// 작업계획서 4.1 (4번): 카카오/네이버/구글. 시각 요소는 각 사 브랜드 아이콘(인라인 SVG)으로 표현(이모지 금지 규칙 준수).
// 클릭 시 전체 페이지 리다이렉트로 OAuth provider 인증 화면으로 이동한다.

import type { ReactNode } from "react";
import { Browser } from "@capacitor/browser";
import { buildSocialStartUrl } from "./authApi.ts";
import { isNativeApp } from "@/shared/platform.ts";

interface ProviderEntry {
  name: "kakao" | "naver" | "google";
  label: string;
  className: string;
  icon: ReactNode;
}

// 카카오 말풍선 아이콘. 노란 버튼 위라 진한 갈색(브랜드색)으로.
function KakaoIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="#3C1E1E">
      <path d="M12 3C6.48 3 2 6.54 2 10.9c0 2.79 1.86 5.24 4.66 6.62-.2.73-.74 2.7-.85 3.12-.13.52.19.51.4.37.16-.11 2.6-1.77 3.66-2.5.69.1 1.4.15 2.13.15 5.52 0 10-3.54 10-7.91S17.52 3 12 3z" />
    </svg>
  );
}

// 네이버 N 레터마크. 초록 버튼 위라 흰색.
function NaverIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="#ffffff">
      <path d="M14.7 12.65 9.05 4H4v16h5.3v-8.65L14.95 20H20V4h-5.3z" />
    </svg>
  );
}

// 구글 4색 G.
function GoogleIcon() {
  return (
    <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  );
}

const PROVIDERS: ReadonlyArray<ProviderEntry> = [
  { name: "kakao", label: "카카오로 시작하기", className: "bg-yellow-300 text-ink-900", icon: <KakaoIcon /> },
  { name: "naver", label: "네이버로 시작하기", className: "bg-green-600 text-white", icon: <NaverIcon /> },
  {
    name: "google",
    label: "구글로 시작하기",
    className: "bg-white text-ink-900 border border-ink-300",
    icon: <GoogleIcon />,
  },
];

// 소셜 로그인 버튼 묶음.
export default function SocialButtons() {
  return (
    <div className="flex flex-col gap-3">
      {PROVIDERS.map((p) => (
        <button
          key={p.name}
          type="button"
          onClick={() => {
            // 네이티브: 인앱 브라우저로 OAuth 를 열고, 콜백은 딥링크(openchuksan://)로 앱에 복귀한다.
            //  - 복귀 이벤트 처리는 NativeBridge(App.appUrlOpen)가 담당한다.
            // 웹: 기존대로 전체 페이지 리다이렉트.
            if (isNativeApp) {
              void Browser.open({ url: buildSocialStartUrl(p.name, true) });
            } else {
              window.location.href = buildSocialStartUrl(p.name);
            }
          }}
          // 아이콘은 왼쪽 고정, 라벨은 가운데 정렬되도록 relative + absolute 아이콘 배치.
          className={`relative flex h-12 items-center justify-center rounded-md text-base font-medium ${p.className}`}
        >
          <span className="absolute left-4 flex items-center" aria-hidden="true">
            {p.icon}
          </span>
          {p.label}
        </button>
      ))}
    </div>
  );
}
